/**
 * BulkProgress — live queue status while a bulk run is in flight.
 *
 * Shows overall progress bar + a compact per-candidate list so the user
 * can watch each one transition from queued → scoring → done.
 */

import { CheckCircle2, Circle, Loader2, XCircle } from "lucide-react";

import type { BulkProgressStats, CandidateSlot } from "@/lib/bulk-match";

interface Props {
  slots: readonly CandidateSlot[];
  stats: BulkProgressStats;
  onAbort?: () => void;
  aborting?: boolean;
}

export function BulkProgress({ slots, stats, onAbort, aborting }: Props) {
  const pct = stats.total === 0 ? 0 : (stats.completedCount / stats.total) * 100;

  return (
    <div className="ta-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-100">
            Scoring {stats.done + stats.errors} / {stats.total}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {stats.scoring > 0 && `${stats.scoring} in flight · `}
            {stats.queued > 0 && `${stats.queued} queued · `}
            {stats.errors > 0 && (
              <span className="text-rose-400">{stats.errors} errors</span>
            )}
          </div>
        </div>
        {onAbort && stats.completedCount < stats.total && (
          <button
            type="button"
            onClick={onAbort}
            disabled={aborting}
            className="text-xs text-slate-400 hover:text-rose-400 transition disabled:opacity-60"
          >
            {aborting ? "Cancelling..." : "Cancel"}
          </button>
        )}
      </div>

      <div className="h-2 bg-slate-800 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>

      <ul className="space-y-1 max-h-[240px] overflow-y-auto pr-1">
        {slots.map((slot) => (
          <li
            key={slot.id}
            className="flex items-center gap-2 text-xs text-slate-400"
          >
            <StatusIcon status={slot.status} />
            <span className="truncate flex-1" title={slot.sourceName}>
              {slot.sourceName}
            </span>
            {slot.status === "done" && slot.result && (
              <span className="text-slate-300 font-mono">
                {Math.round(slot.result.match.overallScore)}
              </span>
            )}
            {slot.status === "error" && (
              <span className="text-rose-400 text-[10px] truncate max-w-[160px]" title={slot.error}>
                {slot.error}
              </span>
            )}
            {slot.status === "scoring" && slot.startedAt && (
              <span className="text-slate-600 text-[10px]">
                {Math.round((performance.now() - slot.startedAt) / 1000)}s
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusIcon({ status }: { status: CandidateSlot["status"] }) {
  if (status === "done") return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />;
  if (status === "error") return <XCircle className="w-3.5 h-3.5 text-rose-400 flex-shrink-0" />;
  if (status === "scoring") return <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin flex-shrink-0" />;
  if (status === "cancelled") return <XCircle className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />;
  return <Circle className="w-3.5 h-3.5 text-slate-700 flex-shrink-0" />;
}
